import { observer } from "mobx-react-lite";
import { TextField, Input, Select, ListBox, ListBoxItem } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import type { LegalFormData } from "@/stores/ContractorDetailStore/legal-store/LegalDataSubStore";

import { FormField } from "../form/FormField";
import { TwoLineSuggestionDropdown } from "../suggestions/TwoLineSuggestionDropdown";
import { latinOnly, numericOnly } from "@/pages/ContractorDetailPage/constants";

const Step1Requisites: React.FC = observer(() => {
  const { legal } = contractorDetailStore;
  const { form, validationErrors: errors } = legal.form;

  const setField = (field: keyof LegalFormData, value: string) =>
    legal.form.setField(field, value);

  return (
    <div
      className="scd-drawer-form"
      data-testid="contractor-detail-legal-step-requisites"
    >
      <FormField
        label="Поиск организации"
        tooltip="Поиск по базе ДаДата. Начните вводить название или ИНН организации"
      >
        <div className="scd-drawer-search-wrap">
          <TextField fullWidth>
            <Input
              placeholder="Начните вводить название или ИНН..."
              value={form.orgSearch}
              onChange={(e) => {
                setField("orgSearch", e.target.value);
                legal.dadata.suggestOrg(e.target.value);
                legal.dadata.setShowOrgSuggestions(true);
              }}
              onFocus={() => {
                if (legal.dadata.orgSuggestions.length > 0) {
                  legal.dadata.setShowOrgSuggestions(true);
                } else if (form.orgSearch.length >= 2) {
                  legal.dadata.suggestOrg(form.orgSearch);
                  legal.dadata.setShowOrgSuggestions(true);
                }
              }}
              onClick={() => {
                if (legal.dadata.orgSuggestions.length > 0) {
                  legal.dadata.setShowOrgSuggestions(true);
                }
              }}
              onBlur={() =>
                setTimeout(() => {
                  legal.dadata.setShowOrgSuggestions(false);
                }, 200)
              }
            />
          </TextField>
          <TwoLineSuggestionDropdown
            suggestions={legal.dadata.orgSuggestions}
            visible={legal.dadata.showOrgSuggestions}
            onSelect={(s) => legal.applyOrgSuggestion(s)}
            getName={(s) => {
              const d = s.data as Record<string, unknown>;
              const n = d.name as Record<string, string> | undefined;
              return n?.short_with_opf ?? s.value;
            }}
            getMeta={(s) => {
              const d = s.data as Record<string, unknown>;
              const addr = (d.address as Record<string, unknown> | undefined)
                ?.value;
              return `ИНН ${String(d.inn ?? "")}${addr ? ` · ${String(addr)}` : ""}`;
            }}
          />
        </div>
      </FormField>
      <FormField
        label="Сокращённое наименование"
        error={errors.legalName}
        required
        tooltip="Краткое наименование организации, например ООО «Компания»"
      >
        <TextField fullWidth>
          <Input
            placeholder="ООО «Название»"
            value={form.legalName}
            onChange={(e) => setField("legalName", e.target.value)}
          />
        </TextField>
      </FormField>
      <FormField
        label="Полное наименование"
        error={errors.legalFullName}
        required
        tooltip="Полное наименование с формой собственности"
      >
        <TextField fullWidth>
          <Input
            placeholder="Общество с ограниченной ответственностью «Название»"
            value={form.legalFullName}
            onChange={(e) => setField("legalFullName", e.target.value)}
          />
        </TextField>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="ИНН"
          error={errors.inn}
          required
          tooltip="Идентификационный номер налогоплательщика. 10 цифр для юр. лиц, 12 для ИП"
        >
          <TextField fullWidth>
            <Input
              placeholder="10 или 12 цифр"
              value={form.inn}
              onChange={(e) =>
                setField("inn", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={12}
            />
          </TextField>
        </FormField>
        <FormField
          label="ОГРН"
          error={errors.ogrn}
          required
          tooltip="Основной государственный регистрационный номер. 13 цифр (ОГРН) или 15 (ОГРНИП)"
        >
          <TextField fullWidth>
            <Input
              placeholder="13 или 15 цифр"
              value={form.ogrn}
              onChange={(e) =>
                setField("ogrn", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={15}
            />
          </TextField>
        </FormField>
      </div>
      <div className="scd-drawer-row">
        <FormField
          label="КПП"
          error={errors.kpp}
          required={form.orgForm !== "ИП"}
          tooltip="Код причины постановки на учёт. 9 цифр. Не заполняется для ИП"
        >
          <TextField fullWidth>
            <Input
              placeholder="9 цифр"
              value={form.kpp}
              onChange={(e) =>
                setField("kpp", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={9}
              disabled={form.orgForm === "ИП"}
            />
          </TextField>
        </FormField>
        <FormField
          label="Форма организации"
          tooltip="Организационно-правовая форма юридического лица"
        >
          <Select
            fullWidth
            placeholder="— Выберите —"
            selectedKey={form.orgForm || null}
            onSelectionChange={(key) =>
              setField("orgForm", (key as string) ?? "")
            }
          >
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                <ListBoxItem id="ООО">ООО</ListBoxItem>
                <ListBoxItem id="ИП">ИП</ListBoxItem>
                <ListBoxItem id="АО">АО</ListBoxItem>
                <ListBoxItem id="ПАО">ПАО</ListBoxItem>
                <ListBoxItem id="ЗАО">ЗАО</ListBoxItem>
                <ListBoxItem id="НКО">НКО</ListBoxItem>
              </ListBox>
            </Select.Popover>
          </Select>
        </FormField>
      </div>
      <FormField
        label="Billing Descriptor"
        error={errors.billingDescriptor}
        required
        tooltip="Название, которое увидит покупатель в SMS и выписке по карте. Латиница, до 14 символов"
      >
        <TextField fullWidth>
          <Input
            placeholder="COMPANY NAME"
            value={form.billingDescriptor}
            onChange={(e) =>
              setField("billingDescriptor", e.target.value.slice(0, 14))
            }
            onKeyDown={latinOnly}
            maxLength={14}
          />
        </TextField>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="E-mail организации"
          error={errors.legalEmail}
          tooltip="E-mail для уведомлений от платёжной системы"
        >
          <TextField fullWidth>
            <Input
              placeholder="info@company.ru"
              value={form.legalEmail}
              onChange={(e) => setField("legalEmail", e.target.value)}
            />
          </TextField>
        </FormField>
        <FormField
          label="Сайт"
          error={errors.siteUrl}
          tooltip="URL сайта или страницы компании в интернете"
        >
          <TextField fullWidth>
            <Input
              placeholder="https://company.ru"
              value={form.siteUrl}
              onChange={(e) => setField("siteUrl", e.target.value)}
            />
          </TextField>
        </FormField>
      </div>
    </div>
  );
});

Step1Requisites.displayName = "Step1Requisites";
export default Step1Requisites;
